import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { FamilyProvider } from './context/FamilyContext';
import ErrorBoundary from './components/ErrorBoundary';
import EnhancedSidebar from './components/EnhancedSidebar';

// Static Imports to prevent 404 on reload
import EnhancedDashboard from './pages/EnhancedDashboard';
import Todos from './pages/Todos';
import Calendar from './pages/Calendar';
import Shopping from './pages/Shopping';
import MealPlanning from './pages/MealPlanning';
import Household from './pages/Household';
import Assets from './pages/Assets';
import Memories from './pages/Memories';
import Documents from './pages/Documents';
import Emergency from './pages/Emergency';
import PublicEmergencyProfile from './pages/PublicEmergencyProfile';
import TrashCalendar from './pages/TrashCalendar';
import NFCScenes from './pages/NFCScenes';
import BlinkCameras from './pages/BlinkCameras';
import Settings from './pages/Settings';
import Login from './pages/Login';
import LandingPage from './pages/LandingPage';
import PartyPlanner from './pages/PartyPlanner';
import PartyGuestView from './pages/PartyGuestView';
import Support from './pages/Support';
import Superadmin from './pages/Superadmin';
import Admin from './pages/Admin';
import BudgetTracker from './pages/BudgetTracker';
import PackedBags from './pages/PackedBags';
import PackedBagPublicView from './pages/PackedBagPublicView';
import FuelPrices from './pages/FuelPrices';
import TabletView from './pages/TabletView';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex h-screen items-center justify-center bg-slate-50">Laden...</div>;
  return user ? children : <Navigate to="/login" />;
};

const AppLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col lg:flex-row">
      <EnhancedSidebar />
      <main className="flex-1 lg:pl-64 pt-16 lg:pt-0 relative z-0">
        {children}
      </main>
    </div>
  );
};

// Layout for the wall mount tablet (no sidebar)
const TabletLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-slate-950">
      {children}
    </div>
  );
};

const App = () => {
  useEffect(() => {
    // Redirect non-hash paths to hash paths to fix 404s on reload
    if (window.location.pathname !== '/' && window.location.pathname !== '/index.html') {
      const path = window.location.pathname;
      window.location.replace(`/#${path}`);
    }
  }, []);

  return (
    <AuthProvider>
      <FamilyProvider>
        <ErrorBoundary>
          <BrowserRouter>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/shared/bag/:id" element={<PackedBagPublicView />} />
              <Route path="/shared/emergency/:token" element={<PublicEmergencyProfile />} />
              <Route path="/party/:id" element={<PartyGuestView />} />

              {/* Private Routes */}
              <Route path="/dashboard" element={<PrivateRoute><AppLayout><EnhancedDashboard /></AppLayout></PrivateRoute>} />
              <Route path="/tablet" element={<PrivateRoute><TabletLayout><TabletView /></TabletLayout></PrivateRoute>} />
              <Route path="/todos" element={<PrivateRoute><AppLayout><Todos /></AppLayout></PrivateRoute>} />
              <Route path="/calendar" element={<PrivateRoute><AppLayout><Calendar /></AppLayout></PrivateRoute>} />
              <Route path="/shopping" element={<PrivateRoute><AppLayout><Shopping /></AppLayout></PrivateRoute>} />
              <Route path="/meals" element={<PrivateRoute><AppLayout><MealPlanning /></AppLayout></PrivateRoute>} />
              <Route path="/household" element={<PrivateRoute><AppLayout><Household /></AppLayout></PrivateRoute>} />
              <Route path="/finance" element={<PrivateRoute><AppLayout><BudgetTracker /></AppLayout></PrivateRoute>} />
              <Route path="/assets" element={<PrivateRoute><AppLayout><Assets /></AppLayout></PrivateRoute>} />
              <Route path="/memories" element={<PrivateRoute><AppLayout><Memories /></AppLayout></PrivateRoute>} />
              <Route path="/documents" element={<PrivateRoute><AppLayout><Documents /></AppLayout></PrivateRoute>} />
              <Route path="/emergency" element={<PrivateRoute><AppLayout><Emergency /></AppLayout></PrivateRoute>} />
              <Route path="/trash" element={<PrivateRoute><AppLayout><TrashCalendar /></AppLayout></PrivateRoute>} />
              <Route path="/nfc" element={<PrivateRoute><AppLayout><NFCScenes /></AppLayout></PrivateRoute>} />
              <Route path="/blink" element={<PrivateRoute><AppLayout><BlinkCameras /></AppLayout></PrivateRoute>} />
              <Route path="/settings" element={<PrivateRoute><AppLayout><Settings /></AppLayout></PrivateRoute>} />
              <Route path="/parties" element={<PrivateRoute><AppLayout><PartyPlanner /></AppLayout></PrivateRoute>} />
              <Route path="/bags" element={<PrivateRoute><AppLayout><PackedBags /></AppLayout></PrivateRoute>} />
              <Route path="/fuel" element={<PrivateRoute><AppLayout><FuelPrices /></AppLayout></PrivateRoute>} />
              <Route path="/support" element={<PrivateRoute><AppLayout><Support /></AppLayout></PrivateRoute>} />
              <Route path="/admin" element={<PrivateRoute><AppLayout><Admin /></AppLayout></PrivateRoute>} />
              <Route path="/superadmin" element={<PrivateRoute><AppLayout><Superadmin /></AppLayout></PrivateRoute>} />
              
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </BrowserRouter>
        </ErrorBoundary>
      </FamilyProvider>
    </AuthProvider>
  );
};

export default App;